/**
 * Auth Store - RBAC session state management
 * Using Zustand with TypeScript (NO persist).
 * La sesión vive en la cookie HttpOnly, NO en localStorage.
 * Se hidrata desde GET /me/profile.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AuthUser, Permission, RoleAssignment, MeProfileResponse } from '@/types/rbac-auth.types';

interface AuthState {
  user: AuthUser | null;
  permissions: Permission[];
  roleAssignments: RoleAssignment[];
  customFields: Record<string, string>;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
  setLoading: () => void;
  setFromProfile: (p: MeProfileResponse) => void;
  clearAuth: () => void;
}

// SIN persist: la sesión vive en la cookie HttpOnly, NO en localStorage.
export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null, permissions: [], roleAssignments: [], customFields: {}, status: 'idle',
      setLoading: () => set({ status: 'loading' }, false, 'setLoading'),
      setFromProfile: (p) => set({
        user: {
          cognitoSub: p.cognitoSub, email: p.email, tenantId: p.tenantId,
          // exactOptionalPropertyTypes: incluir opcionales solo cuando vienen definidos
          ...(p.displayName !== undefined ? { displayName: p.displayName } : {}),
          ...(p.phone !== undefined ? { phone: p.phone } : {}),
          ...(p.mfaSetupRequired !== undefined ? { mfaSetupRequired: p.mfaSetupRequired } : {}),
        },
        permissions: p.permissions, roleAssignments: p.roleAssignments,
        customFields: p.customFields ?? {}, status: 'authenticated',
      }, false, 'setFromProfile'),
      clearAuth: () => set({ user: null, permissions: [], roleAssignments: [],
                            customFields: {}, status: 'unauthenticated' }, false, 'clearAuth'),
    }),
    { name: 'AuthStore' },
  ),
);
